/**
 * CheckersBoard React Component
 *
 * Renders an interactive 8x8 checkers board with pieces on dark squares,
 * legal move highlighting, mandatory capture indicators, multi-jump support,
 * move animations, and responsive design for desktop and mobile.
 *
 * Requirements: 1.1, 1.5, 1.6, 2.5, 3.6, 4.3, 5.3, 6.2, 18.1–18.6,
 *               21.1–21.4, 23.1, 23.3, 23.5, 35.1–35.5
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CheckersEngine } from '../CheckersEngine';
import {
  Position,
  CheckersPiece,
  CheckersMove,
  CheckersGameResult,
  PlayerColor,
  positionsEqual,
  isDarkSquare,
} from '../types';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import './CheckersBoard.css';

export interface CheckersBoardProps {
  engine: CheckersEngine;
  orientation?: PlayerColor;
  interactive?: boolean;
  isAIThinking?: boolean;
  onMove?: (move: CheckersMove) => void;
  onGameEnd?: (result: CheckersGameResult) => void;
  moveCount?: number;
}

interface CheckersBoardState {
  selectedPiece?: Position;
  legalMoves: Position[];
  lastMove?: CheckersMove;
  highlightedSquares: Position[];
  mandatoryCaptureSquares: Position[];
  multiJumpInProgress: boolean;
  animatingMove?: {
    from: Position;
    to: Position;
    piece: CheckersPiece;
  };
  captureAnimation?: {
    position: Position;
  };
  promotionAnimation?: {
    position: Position;
  };
  announcement: string;
}

/**
 * CheckersBoard component renders the checkers board and handles user interactions.
 */
export const CheckersBoard: React.FC<CheckersBoardProps> = ({
  engine,
  orientation = 'dark',
  interactive = true,
  isAIThinking = false,
  onMove,
  onGameEnd,
  moveCount,
}) => {
  const [state, setState] = useState<CheckersBoardState>({
    legalMoves: [],
    highlightedSquares: [],
    mandatoryCaptureSquares: [],
    multiJumpInProgress: false,
    announcement: '',
  });

  const boardRef = useRef<HTMLDivElement>(null);

  // Get current game state
  const gameState = engine.getGameState();
  const board = gameState.board;
  const currentPlayer = gameState.currentPlayer;
  const gameStatus = gameState.gameStatus;

  // Update last move when move history changes
  useEffect(() => {
    const moveHistory = engine.getMoveHistory();
    if (moveHistory.length > 0) {
      setState((prev) => ({
        ...prev,
        lastMove: moveHistory[moveHistory.length - 1],
      }));
    }
  }, [engine, gameState.moveHistory.length]);

  // Update mandatory capture squares and multi-jump state
  useEffect(() => {
    if (gameStatus.type !== 'active') return;

    const multiJumpInProgress = engine.isMultiJumpInProgress();
    const mandatoryCaptureSquares: Position[] = [];

    if (!multiJumpInProgress && engine.hasJumpMoves(currentPlayer)) {
      // Find all pieces that have jump moves
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const piece = board[row][col];
          if (piece && piece.color === currentPlayer) {
            const jumps = engine.getJumpMoves({ row, col });
            if (jumps.length > 0) {
              mandatoryCaptureSquares.push({ row, col });
            }
          }
        }
      }
    }

    setState((prev) => ({
      ...prev,
      mandatoryCaptureSquares,
      multiJumpInProgress,
    }));
  }, [engine, board, currentPlayer, gameStatus, moveCount]);

  // Check for game end
  useEffect(() => {
    if (gameStatus.type !== 'active' && onGameEnd) {
      if (gameStatus.type === 'win') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          winner: gameStatus.winner,
          isDraw: false,
          reason: gameStatus.reason,
          moveCount: gameState.moveHistory.length,
          duration: 0,
          completedAt: Date.now(),
        });
      } else if (gameStatus.type === 'draw') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          isDraw: true,
          reason: gameStatus.reason,
          moveCount: gameState.moveHistory.length,
          duration: 0,
          completedAt: Date.now(),
        });
      } else if (gameStatus.type === 'resignation') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          winner: gameStatus.winner,
          isDraw: false,
          reason: 'resignation',
          moveCount: gameState.moveHistory.length,
          duration: 0,
          completedAt: Date.now(),
        });
      }
    }
  }, [gameStatus, onGameEnd, gameState.moveHistory.length]);

  /**
   * Selects a piece and highlights its legal moves.
   */
  const selectPiece = useCallback(
    (position: Position) => {
      const legalMoves = engine.getLegalMoves(position);

      setState((prev) => ({
        ...prev,
        selectedPiece: position,
        legalMoves,
        highlightedSquares: [position, ...legalMoves],
      }));
    },
    [engine]
  );

  /**
   * Executes a move from one position to another with animation.
   */
  const executeMove = useCallback(
    (from: Position, to: Position) => {
      const piece = board[from.row][from.col];
      if (!piece) return;

      const isJump =
        Math.abs(to.row - from.row) === 2 && Math.abs(to.col - from.col) === 2;
      const capturedPos = isJump
        ? {
            row: (from.row + to.row) / 2,
            col: (from.col + to.col) / 2,
          }
        : undefined;

      // Start animation
      setState((prev) => ({
        ...prev,
        selectedPiece: undefined,
        legalMoves: [],
        highlightedSquares: [],
        animatingMove: { from, to, piece },
        captureAnimation: capturedPos ? { position: capturedPos } : undefined,
      }));

      // Execute move after animation delay
      setTimeout(() => {
        const result = engine.makeMove(from, to);

        if (result.success && result.move) {
          // Check if promotion happened
          const promoted = result.move.isPromotion;

          // Build announcement
          let announcement = `${piece.color} moved from ${from.row},${from.col} to ${to.row},${to.col}`;
          if (isJump) announcement += '. Capture!';
          if (promoted) announcement += '. Piece promoted to king!';
          if (result.multiJumpContinuation) announcement += '. Continue jumping.';

          setState((prev) => ({
            ...prev,
            lastMove: result.move,
            animatingMove: undefined,
            captureAnimation: undefined,
            promotionAnimation: promoted ? { position: to } : undefined,
            announcement,
          }));

          // Clear promotion animation after it plays
          if (promoted) {
            setTimeout(() => {
              setState((prev) => ({
                ...prev,
                promotionAnimation: undefined,
              }));
            }, 500);
          }

          // If multi-jump continuation, auto-select the piece
          if (result.multiJumpContinuation) {
            const continuationMoves = result.continuationMoves || [];
            setState((prev) => ({
              ...prev,
              selectedPiece: to,
              legalMoves: continuationMoves,
              highlightedSquares: [to, ...continuationMoves],
              multiJumpInProgress: true,
            }));
          }

          // Notify parent component
          if (onMove && !result.multiJumpContinuation) {
            onMove(result.move);
          }

          // Haptic feedback
          if ('vibrate' in navigator) {
            navigator.vibrate(15);
          }
        } else {
          // Move failed — clear animation
          setState((prev) => ({
            ...prev,
            animatingMove: undefined,
            captureAnimation: undefined,
          }));
        }
      }, 300);
    },
    [board, engine, onMove]
  );

  /**
   * Handles square click for piece selection and movement.
   */
  const handleSquareClick = useCallback(
    (position: Position) => {
      if (!interactive || isAIThinking) return;
      if (gameStatus.type !== 'active') return;

      const piece = board[position.row]?.[position.col];

      // If a piece is already selected
      if (state.selectedPiece) {
        const isLegalMove = state.legalMoves.some((m) =>
          positionsEqual(m, position)
        );

        if (isLegalMove) {
          executeMove(state.selectedPiece, position);
        } else if (piece && piece.color === currentPlayer) {
          // During multi-jump, don't allow selecting a different piece
          if (state.multiJumpInProgress) return;
          selectPiece(position);
        } else {
          // Deselect (unless multi-jump in progress)
          if (!state.multiJumpInProgress) {
            setState((prev) => ({
              ...prev,
              selectedPiece: undefined,
              legalMoves: [],
              highlightedSquares: [],
            }));
          }
        }
      } else {
        // No piece selected — try to select one
        if (piece && piece.color === currentPlayer) {
          // During multi-jump, only allow selecting the multi-jump piece
          if (engine.isMultiJumpInProgress()) {
            const multiJumpPiece = engine.getMultiJumpPiece();
            if (multiJumpPiece && !positionsEqual(position, multiJumpPiece)) {
              return;
            }
          }
          selectPiece(position);
        }
      }
    },
    [
      interactive,
      isAIThinking,
      gameStatus,
      board,
      currentPlayer,
      state.selectedPiece,
      state.legalMoves,
      state.multiJumpInProgress,
      engine,
      executeMove,
      selectPiece,
    ]
  );

  /**
   * Handles touch start for mobile interaction.
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, position: Position) => {
      if (!interactive || isAIThinking) return;
      e.preventDefault();
      handleSquareClick(position);
    },
    [interactive, isAIThinking, handleSquareClick]
  );

  /**
   * Determines CSS classes for a square.
   */
  const getSquareClasses = (row: number, col: number): string => {
    const position: Position = { row, col };
    const classes: string[] = ['checkers-square'];

    // Alternating colors
    const isDark = isDarkSquare(position);
    classes.push(isDark ? 'checkers-square--dark' : 'checkers-square--light');

    // Selected piece
    if (
      state.selectedPiece &&
      positionsEqual(state.selectedPiece, position)
    ) {
      classes.push('checkers-square--selected');
    }

    // Legal move highlight
    if (state.legalMoves.some((m) => positionsEqual(m, position))) {
      const targetPiece = board[row]?.[col];
      // If there's a piece at the destination, it's a capture indicator
      // For checkers jumps, the destination is always empty, but we show
      // capture indicator for jump moves (distance 2)
      const isJumpDest =
        state.selectedPiece &&
        Math.abs(row - state.selectedPiece.row) === 2;
      classes.push(
        isJumpDest ? 'checkers-square--capture' : 'checkers-square--legal'
      );
    }

    // Last move highlight
    if (state.lastMove) {
      if (
        positionsEqual(state.lastMove.from, position) ||
        positionsEqual(state.lastMove.to, position)
      ) {
        classes.push('checkers-square--last-move');
      }
    }

    // Mandatory capture highlight
    if (
      state.mandatoryCaptureSquares.some((m) => positionsEqual(m, position))
    ) {
      // Only show if this square is not already selected
      if (
        !state.selectedPiece ||
        !positionsEqual(state.selectedPiece, position)
      ) {
        classes.push('checkers-square--mandatory-capture');
      }
    }

    // Multi-jump continuation highlight
    if (
      state.multiJumpInProgress &&
      state.legalMoves.some((m) => positionsEqual(m, position))
    ) {
      classes.push('checkers-square--multi-jump');
    }

    // Capture animation
    if (
      state.captureAnimation &&
      positionsEqual(state.captureAnimation.position, position)
    ) {
      classes.push('checkers-square--capturing');
    }

    return classes.join(' ');
  };

  /**
   * Gets CSS classes for a piece including animation states.
   */
  const getPieceClasses = (
    position: Position,
    piece: CheckersPiece
  ): string => {
    const classes: string[] = ['checkers-piece', `checkers-piece--${piece.color}`];

    // Moving animation
    if (
      state.animatingMove &&
      positionsEqual(state.animatingMove.from, position)
    ) {
      classes.push('checkers-piece--moving');
    }

    // Captured animation
    if (
      state.captureAnimation &&
      positionsEqual(state.captureAnimation.position, position)
    ) {
      classes.push('checkers-piece--captured');
    }

    // Promotion animation
    if (
      state.promotionAnimation &&
      positionsEqual(state.promotionAnimation.position, position)
    ) {
      classes.push('checkers-piece--promoting');
    }

    return classes.join(' ');
  };

  /**
   * Renders the board grid.
   */
  const renderBoard = () => {
    // Row order depends on orientation
    // 'dark' at bottom means row 0 at top, row 7 at bottom (default)
    // 'light' at bottom means row 7 at top, row 0 at bottom
    const rows =
      orientation === 'dark'
        ? [0, 1, 2, 3, 4, 5, 6, 7]
        : [7, 6, 5, 4, 3, 2, 1, 0];
    const cols =
      orientation === 'dark'
        ? [0, 1, 2, 3, 4, 5, 6, 7]
        : [7, 6, 5, 4, 3, 2, 1, 0];

    return (
      <div className="checkers-board-container">
        <div className="checkers-board" ref={boardRef}>
          <div className="checkers-board__grid">
            {rows.map((row, rowIdx) =>
              cols.map((col, colIdx) => {
                const position: Position = { row, col };
                const piece = board[row]?.[col] ?? null;
                const squareKey = `${row}-${col}`;

                return (
                  <div
                    key={squareKey}
                    className={getSquareClasses(row, col)}
                    onClick={() => handleSquareClick(position)}
                    onTouchStart={(e) => handleTouchStart(e, position)}
                    data-square={squareKey}
                    role="button"
                    tabIndex={isDarkSquare(position) ? 0 : -1}
                    aria-label={getSquareAriaLabel(row, col, piece)}
                  >
                    {piece && (
                      <div className={getPieceClasses(position, piece)}>
                        {piece.type === 'king' && (
                          <span
                            className="checkers-piece__crown"
                            aria-hidden="true"
                          >
                            ♛
                          </span>
                        )}
                      </div>
                    )}

                    {/* Row coordinate on first column */}
                    {colIdx === 0 && (
                      <span className="checkers-coordinate checkers-coordinate--rank">
                        {row + 1}
                      </span>
                    )}
                    {/* Column coordinate on last row */}
                    {rowIdx === 7 && (
                      <span className="checkers-coordinate checkers-coordinate--file">
                        {String.fromCharCode(97 + col)}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* AI Thinking Indicator */}
          {isAIThinking && (
            <div className="checkers-board__ai-thinking">
              <LoadingIndicator
                variant="dots"
                size="medium"
                color="primary"
                message="AI is thinking..."
              />
            </div>
          )}

          {/* Game Over Overlay */}
          {renderGameOverOverlay()}

          {/* ARIA Live Region for announcements */}
          <div
            className="checkers-board__announcer"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {state.announcement}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Generates an ARIA label for a board square.
   */
  const getSquareAriaLabel = (
    row: number,
    col: number,
    piece: CheckersPiece | null
  ): string => {
    const colLetter = String.fromCharCode(97 + col);
    const squareName = `${colLetter}${row + 1}`;
    const squareColor = isDarkSquare({ row, col }) ? 'dark' : 'light';

    if (piece) {
      const pieceDesc = `${piece.color} ${piece.type}`;
      return `${squareName}, ${squareColor} square, ${pieceDesc}`;
    }
    return `${squareName}, ${squareColor} square, empty`;
  };

  /**
   * Renders the game over overlay.
   */
  const renderGameOverOverlay = () => {
    if (gameStatus.type === 'active') return null;

    if (gameStatus.type === 'win') {
      return (
        <div className="checkers-board__game-over">
          <div className="checkers-board__game-over-content">
            <div className="checkers-board__game-over-icon">🏆</div>
            <div className="checkers-board__game-over-title">
              {gameStatus.winner === 'dark' ? 'Dark' : 'Light'} Wins!
            </div>
            <div className="checkers-board__game-over-message">
              {gameStatus.reason === 'capture-all'
                ? 'All opponent pieces captured'
                : 'Opponent has no legal moves'}
            </div>
          </div>
        </div>
      );
    }

    if (gameStatus.type === 'draw') {
      return (
        <div className="checkers-board__game-over">
          <div className="checkers-board__game-over-content">
            <div className="checkers-board__game-over-icon">🤝</div>
            <div className="checkers-board__game-over-title">Draw</div>
            <div className="checkers-board__game-over-message">
              {gameStatus.reason === '40-move' && '40-move rule'}
              {gameStatus.reason === 'repetition' && 'Threefold repetition'}
              {gameStatus.reason === 'insufficient-progress' &&
                'Insufficient progress'}
            </div>
          </div>
        </div>
      );
    }

    if (gameStatus.type === 'resignation') {
      return (
        <div className="checkers-board__game-over">
          <div className="checkers-board__game-over-content">
            <div className="checkers-board__game-over-icon">🏳️</div>
            <div className="checkers-board__game-over-title">
              {gameStatus.winner === 'dark' ? 'Dark' : 'Light'} Wins!
            </div>
            <div className="checkers-board__game-over-message">
              Opponent resigned
            </div>
          </div>
        </div>
      );
    }

    if (gameStatus.type === 'draw-agreement') {
      return (
        <div className="checkers-board__game-over">
          <div className="checkers-board__game-over-content">
            <div className="checkers-board__game-over-icon">🤝</div>
            <div className="checkers-board__game-over-title">Draw</div>
            <div className="checkers-board__game-over-message">
              Draw by agreement
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return renderBoard();
};

export default CheckersBoard;
